import React, { useState, useEffect } from 'react';
import { IoInformationCircleOutline, IoCheckmarkOutline, IoPlayCircleOutline, IoBookOutline, IoCopyOutline } from 'react-icons/io5';
import { HiChevronRight } from 'react-icons/hi';
import { Link } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

export default function Deposit() {
    const { t } = useTranslation();
    const [selectedCoin, setSelectedCoin] = useState('ETH');
    const [selectedChain, setSelectedChain] = useState('Ethereum');
    const [riskAccepted, setRiskAccepted] = useState(false);
    const [copiedState, setCopiedState] = useState(false);
    const [userInfo, setUserInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [cryptoPaymentMethods, setCryptoPaymentMethods] = useState([]);
    const [methodsLoading, setMethodsLoading] = useState(true);

    // Coin verileri
    const coins = [
        {
            symbol: 'ETH',
            name: 'Ethereum',
            icon: '/assets/images/cryptos/ethereum-eth-logo.png',
            networks: [
                { name: 'Ethereum', address: '0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6' },
                { name: 'Polygon', address: '0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b7' },
                { name: 'BSC', address: '0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b8' }
            ]
        },
        {
            symbol: 'BTC',
            name: 'Bitcoin',
            icon: '/assets/images/cryptos/bitcoin-btc-logo.png',
            networks: [
                { name: 'Ethereum', address: '0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6' },
                { name: 'Polygon', address: '0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b7' },
                { name: 'BSC', address: '0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b8' }
            ]
        },
        {
            symbol: 'SOL',
            name: 'Solana',
            icon: '/assets/images/cryptos/solana-sol-logo.png',
            networks: [
                { name: 'Solana', address: '9WzDXwBbmkg8ZTbNMqUxvQRAyrZzDsGYdLVL9zYtAWWM' }
            ]
        },
        {
            symbol: 'USDT',
            name: 'Tether',
            icon: '/assets/images/cryptos/tether-usdt-logo.png',
            networks: [
                { name: 'Ethereum', address: '0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b9' },
                { name: 'Tron', address: 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t' },
                { name: 'BSC', address: '0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8ba' }
            ]
        }
    ];

    const selectedCoinData = coins.find(coin => coin.symbol === selectedCoin);

    // Get user info on component mount
    useEffect(() => {
        const fetchUserInfo = async () => {
            try {
                setLoading(true);
                const response = await api.getUserInfo();
                const userData = response.data?.data || response.data;
                // Map camelCase to PascalCase for consistency
                const normalizedData = {
                    ...userData,
                    Balance: userData.Balance ?? userData.balance,
                    Credit: userData.Credit ?? userData.credit,
                    FullName: userData.FullName ?? userData.fullName
                };
                setUserInfo(normalizedData);
            } catch (error) {
                console.error('Error fetching user info:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchUserInfo();
    }, []);

    // BITCOIN → BTC gibi uzun isimleri sembol kısaltmasına çevirir
    const normalizeAsset = (asset) => {
        const map = { 'BITCOIN': 'BTC', 'ETHEREUM': 'ETH', 'SOLANA': 'SOL', 'TETHER': 'USDT' };
        return map[asset?.toUpperCase()] || asset;
    };

    // Get crypto payment methods
    useEffect(() => {
        const fetchCryptoPaymentMethods = async () => {
            try {
                setMethodsLoading(true);
                const response = await api.getCryptoPaymentMethods();
                const data = response.data;
                if (data.success && data.data) {
                    const normalized = data.data.map(m => ({ ...m, asset: normalizeAsset(m.asset) }));
                    setCryptoPaymentMethods(normalized);
                    if (normalized.length > 0) {
                        setSelectedCoin(normalized[0].asset);
                        setSelectedChain(normalized[0].network);
                    }
                }
            } catch (error) {
                console.error('Error fetching crypto payment methods:', error);
            } finally {
                setMethodsLoading(false);
            }
        };

        fetchCryptoPaymentMethods();
    }, []);

    const handleCoinChange = (e) => {
        setSelectedCoin(e.target.value);
        // İlk network'ü seç
        if (selectedCoinData) {
            setSelectedChain(selectedCoinData.networks[0].name);
        }
    };

    const handleNetworkChange = (networkName) => {
        setSelectedChain(networkName);
    };

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success(t('deposit.toast.addressCopied'));

            // Tik işareti göster
            setCopiedState(true);

            // 3 saniye sonra eski haline döndür
            setTimeout(() => {
                setCopiedState(false);
            }, 3000);
        } catch (err) {
            console.error('Kopyalama hatası:', err);
            toast.error(t('deposit.toast.copyFailed'));
        }
    };

    return (
        <div className='deposit-layout'>
            <div className="container py-lg-5">
                <div className="row justify-content-center">
                    <div className="col-lg-10">
                        <div className="deposit-page">
                            {/* Header */}
                            <div className="deposit-header">
                                <div className="d-flex align-items-center justify-content-between">
                                    <div className="d-flex align-items-center gap-3">
                                        <h1 className="deposit-title">{t('deposit.title')}</h1>
                                    </div>
                                    <Link to="/deposit-bank" className="p2p-link d-flex align-items-center gap-2">
                                        <span>{t('deposit.bankTransferLink')}</span>
                                        <HiChevronRight size={16} />
                                    </Link>
                                </div>
                            </div>

                            <div className="row g-4">
                                {/* Sol Kolon - Yatırma İşlemleri */}
                                <div className="container">
                                    {/* Yatay Varlıklar Kartı */}
                                    <div className="assets-card-horizontal">
                                        <div className="assets-horizontal-grid">
                                            <div className="asset-balance-item left">
                                                <div className="asset-balance-label">
                                                    <span className="asset-dot available"></span>
                                                    {t('deposit.assets.title')}
                                                </div>
                                                <div className="asset-balance-value">
                                                    {loading ? '...' : `${((userInfo?.Balance || 0) + (userInfo?.Credit || 0)).toFixed(2)} USD`}
                                                </div>
                                            </div>

                                            <div className="asset-total-center">
                                                <div className="asset-total-label">{t('deposit.assets.availableBalance')}</div>
                                                <div className="asset-total-amount">
                                                    <span>{loading ? '...' : `${(userInfo?.Balance || 0).toFixed(2)} USD`}</span>
                                                </div>
                                            </div>

                                            <div className="asset-balance-item right">
                                                <div className="asset-balance-label">
                                                    <span className="asset-dot frozen"></span>
                                                    {t('deposit.assets.availableCredit')}
                                                </div>
                                                <div className="asset-balance-value">
                                                    <span>{loading ? '...' : `${(userInfo?.Credit || 0).toFixed(2)} USD`}</span>
                                                </div>  
                                            </div>
                                        </div>
                                    </div>

                                    <div className="deposit-actions">
                                        {/* 1. Coin Seçimi */}
                                        <div className="deposit-step">
                                            <div className="step-number">1</div>
                                            <div className="step-content">
                                                <h3 className="step-title">{t('deposit.steps.selectCoin')}</h3>
                                                <div className="coin-selector mt-lg-3">
                                                    <div className="coin-options">
                                                        {methodsLoading ? (
                                                            <div className="text-center py-3">
                                                                <div className="spinner-border text-primary" role="status">
                                                                    <span className="visually-hidden">Loading...</span>
                                                                </div>
                                                                <p className="mt-2">Loading payment methods...</p>
                                                            </div>
                                                        ) : (
                                                            cryptoPaymentMethods.map((method) => {
                                                                const coinData = coins.find(coin => coin.symbol === method.asset);
                                                                return (
                                                                    <button
                                                                        key={method.id}
                                                                        className={`coin-option ${selectedCoin === method.asset ? 'active' : ''}`}
                                                                        onClick={() => setSelectedCoin(method.asset)}
                                                                    >
                                                                        <img src={coinData?.icon || '/assets/images/default-coin.png'} alt={method.asset} width="30" height="30" />
                                                                        <div className="coin-option-info">
                                                                            <span className="coin-option-symbol">{method.asset}</span>
                                                                            <span className="coin-option-name">{coinData?.name || method.asset}</span>
                                                                        </div>
                                                                    </button>
                                                                );
                                                            })
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* 2. Network ve Cüzdan Adresi */}
                                        <div className="deposit-step">
                                            <div className="step-number">2</div>
                                            <div className="step-content">
                                                <h3 className="step-title">{t('deposit.steps.networkAndAddress')}</h3>

                                                {/* Seçilen Coin Bilgisi */}
                                                <div className="selected-coin-info mt-lg-3">
                                                    <div className="coin-display">
                                                        <img src={selectedCoinData?.icon} alt={selectedCoinData?.symbol} width="32" height="32" />
                                                        <div className="coin-details">
                                                            <span className="coin-symbol">{selectedCoinData?.symbol}</span>
                                                            <span className="coin-name">{selectedCoinData?.name}</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Network Seçimi */}
                                                <div className="network-selector">
                                                    <div className="network-label">
                                                        <span>{t('deposit.networkSelection.selectNetwork')}</span>
                                                        <IoInformationCircleOutline size={16} className="info-icon" />
                                                    </div>
                                                    <div className="network-options">
                                                        {cryptoPaymentMethods
                                                            .filter(method => method.asset === selectedCoin)
                                                            .map((method) => (
                                                                <button
                                                                    key={method.id}
                                                                    className={`network-option ${selectedChain === method.network ? 'active' : ''}`}
                                                                    onClick={() => handleNetworkChange(method.network)}
                                                                >
                                                                    {method.network}
                                                                </button>
                                                            ))}
                                                    </div>
                                                </div>

                                                {/* Cüzdan Adresi */}
                                                <div className="wallet-address">
                                                    <div className="section-label">{t('deposit.networkSelection.walletAddress')}</div>
                                                    <div className="address-display">
                                                        <span className="address-text">
                                                            {cryptoPaymentMethods
                                                                .find(method => method.asset === selectedCoin && method.network === selectedChain)
                                                                ?.walletAddress || ''}
                                                        </span>
                                                        <button
                                                            className={`copy-btn ${copiedState ? 'copied' : ''}`}
                                                            onClick={() => copyToClipboard(
                                                                cryptoPaymentMethods
                                                                    .find(method => method.asset === selectedCoin && method.network === selectedChain)
                                                                    ?.walletAddress || ''
                                                            )}
                                                        >
                                                            {copiedState ? <IoCheckmarkOutline size={16} /> : <IoCopyOutline size={16} />}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Risk Uyarısı */}
                                        <div className="risk-warning">
                                            <h4 className="risk-title">{selectedCoinData?.symbol}-{selectedCoinData?.name} {t('deposit.riskWarning.title')}</h4>
                                            <p className="risk-text">
                                                {t('deposit.riskWarning.description')}
                                            </p>
                                            {/* <div className="risk-checkbox">
                                                <input
                                                    type="checkbox"
                                                    id="risk-accept"
                                                    checked={riskAccepted}
                                                    onChange={(e) => setRiskAccepted(e.target.checked)}
                                                />
                                                <label htmlFor="risk-accept">
                                                    {t('deposit.riskWarning.acceptRisk')}
                                                </label>
                                            </div> */}
                                        </div>

                                        {/* Onayla Butonu */}
                                        {/* <div className="deposit-actions-footer">
                                            <button
                                                className={`confirm-btn ${riskAccepted ? 'active' : 'disabled'}`}
                                                disabled={!riskAccepted}
                                            >
                                                {t('deposit.confirmButton')}
                                            </button>
                                        </div> */}
                                    </div>
                                </div>

                                {/* Sağ Kolon - Bilgiler */}
                                {/* <div className="col-lg-4">
                                    <div className="deposit-info"> */}
                                        
                                        {/* <div className="info-section">
                                            <h3 className="info-title">{t('deposit.assets.title')}</h3>
                                            <div className="assets-summary">
                                                <div className="total-assets">
                                                    {loading ? '...' : `${((userInfo?.Balance || 0) + (userInfo?.Credit || 0)).toFixed(2)} USD`}
                                                </div>
                                                <div className="assets-breakdown">
                                                    <div className="asset-item">
                                                        <div className="asset-dot available"></div>
                                                        <span>{t('deposit.assets.availableBalance')}:</span>
                                                        <span>{loading ? '...' : `${(userInfo?.Balance || 0).toFixed(2)} USD`}</span>
                                                    </div>
                                                    <div className="asset-item">
                                                        <div className="asset-dot frozen"></div>
                                                        <span>{t('deposit.assets.availableCredit')}:</span>
                                                        <span>{loading ? '...' : `${(userInfo?.Credit || 0).toFixed(2)} USD`}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div> */}

                                        {/* Dikkat */}
                                        {/* <div className="info-section">
                                            <h3 className="info-title">{t('deposit.attention.title')}</h3>
                                            <div className="attention-items">
                                                <div className="attention-item">
                                                    <span className="attention-label">{t('deposit.attention.minDeposit')}</span>
                                                    <span className="attention-value">0.0001 BTC</span>
                                                </div>
                                                <div className="attention-item">
                                                    <span className="attention-label">{t('deposit.attention.confirmationBlocks')}</span>
                                                    <span className="attention-value">1 Blok Onayı</span>
                                                </div>
                                                <div className="attention-item">
                                                    <span className="attention-label">{t('deposit.attention.withdrawalConfirmationBlocks')}</span>
                                                    <span className="attention-value">6 Blok Onayı</span>
                                                </div>
                                            </div>
                                        </div> */}

                                        {/* SSS */}
                                        {/* <div className="info-section">
                                            <div className="faq-header">
                                                <h3 className="info-title">{t('deposit.faq.title')}</h3>
                                            </div>
                                            <div className="faq-items">
                                                <div className="faq-item">
                                                    <div className="faq-question">
                                                        <IoInformationCircleOutline size={16} className="faq-icon" />
                                                        <span>{t('deposit.faq.verificationNotRequired')}</span>
                                                    </div>
                                                    <button className="self-recovery-btn">{t('deposit.faq.selfRecovery')}</button>
                                                </div>
                                                <div className="faq-item">
                                                    <div className="faq-question">
                                                        <IoInformationCircleOutline size={16} className="faq-icon" />
                                                        <span>{t('deposit.faq.depositGuide')}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="help-links">
                                                <Link to="/beginner-guide" className="help-link">
                                                    <IoBookOutline size={16} />
                                                    <span>{t('deposit.helpLinks.beginnerGuide')}</span>
                                                </Link>
                                                <Link to="/tutorial" className="help-link">
                                                    <IoPlayCircleOutline size={16} />
                                                    <span>{t('deposit.helpLinks.tutorialVideo')}</span>
                                                </Link>
                                            </div>
                                        </div> */}
                                    {/* </div>
                                </div> */}
                            </div>
                        </div>
                    </div>
                </div>
                <Toaster
                    position="top-center"
                    toastOptions={{
                        duration: 4000,
                        style: {
                            background: '#363636',
                            color: '#fff',
                        },
                        success: {
                            duration: 3000,
                            style: {
                                background: '#10B981',
                                color: '#fff',
                            },
                        },
                        error: {
                            duration: 5000,
                            style: {
                                background: '#EF4444',
                                color: '#fff',
                            },
                        },
                    }}
                />
            </div>
        </div>
    );
}
